import React, { useEffect, useMemo, useState } from 'react';
import { Link, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useMenuVisibility } from '../../contexts/MenuVisibilityContext';
import { useThemeMode } from '../../contexts/ThemeContext';

const Icon = ({ name, className = 'h-6 w-6' }) => {
  const props = {
    className,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: '1.75',
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    'aria-hidden': true,
  };
  switch (name) {
    case 'home':
      return (
        <svg {...props}>
          <path d="M3 10.5L12 3l9 7.5" />
          <path d="M5 9.5V21h14V9.5" />
        </svg>
      );
    case 'belts':
      return (
        <svg {...props}>
          <circle cx="12" cy="12" r="8" />
          <path d="M8 12h8" />
        </svg>
      );
    case 'events':
      return (
        <svg {...props}>
          <rect x="3" y="5" width="18" height="16" rx="2" />
          <path d="M3 10h18M8 3v4M16 3v4" />
        </svg>
      );
    case 'students':
      return (
        <svg {...props}>
          <circle cx="12" cy="8" r="3.5" />
          <path d="M5 20c1.5-3.5 4-5 7-5s5.5 1.5 7 5" />
        </svg>
      );
    case 'contact':
      return (
        <svg {...props}>
          <path d="M4 6h16v12H4z" />
          <path d="M4 7l8 6 8-6" />
        </svg>
      );
    case 'more':
      return (
        <svg {...props}>
          <circle cx="6" cy="12" r="1.5" fill="currentColor" stroke="none" />
          <circle cx="12" cy="12" r="1.5" fill="currentColor" stroke="none" />
          <circle cx="18" cy="12" r="1.5" fill="currentColor" stroke="none" />
        </svg>
      );
    default:
      return null;
  }
};

const PublicHeader = () => {
  const { isAuthenticated, logout } = useAuth();
  const { menuItems: menuLinks } = useMenuVisibility();
  const { mode, toggleTheme } = useThemeMode();
  const navigate = useNavigate();
  const location = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);

  const bottomPrimary = useMemo(
    () => menuLinks.filter((l) => l.show_in_bottom_nav).slice(0, 4),
    [menuLinks],
  );
  const moreLinks = useMemo(() => {
    const primaryPaths = new Set(bottomPrimary.map((l) => l.path));
    return menuLinks.filter((l) => !primaryPaths.has(l.path));
  }, [menuLinks, bottomPrimary]);

  useEffect(() => {
    setMoreOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = moreOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [moreOpen]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const desktopLinkClass = ({ isActive }) =>
    `min-h-9 px-2 text-sm font-medium transition-colors duration-200 hover:text-primary ${
      isActive ? 'text-primary' : 'text-ink'
    }`;

  const bottomLinkClass = ({ isActive }) =>
    `flex min-h-[3.25rem] flex-1 flex-col items-center justify-center gap-0.5 text-[10px] font-semibold uppercase tracking-wide transition-colors ${
      isActive ? 'text-primary' : 'text-on-chrome/70'
    }`;

  return (
    <>
      <header className="sticky top-0 z-40 border-b border-line/80 bg-surface/95 backdrop-blur-sm">
        <div className="mx-auto flex h-12 max-w-content items-center justify-between gap-3 px-3 md:h-14 md:px-6 lg:px-8">
          <Link
            to="/"
            className="flex min-h-9 items-center gap-2 font-display text-base font-bold uppercase tracking-wide text-ink md:text-xl"
          >
            <img
              src={`${process.env.PUBLIC_URL}/logo192.png`}
              alt=""
              className="h-7 w-7 rounded-full object-cover md:h-8 md:w-8"
              width={32}
              height={32}
            />
            <span>Team Kitsune</span>
          </Link>

          <nav className="hidden items-center gap-0.5 lg:flex" aria-label="Principal">
            {menuLinks.map((link) => (
              <NavLink key={link.slug} to={link.path} end={link.path === '/'} className={desktopLinkClass}>
                {link.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-1">
            <button
              type="button"
              className="inline-flex min-h-9 min-w-9 items-center justify-center rounded-md text-ink hover:bg-canvas hover:text-primary"
              onClick={toggleTheme}
              aria-label={mode === 'dark' ? 'Modo claro' : 'Modo oscuro'}
            >
              {mode === 'dark' ? '☀' : '☾'}
            </button>
            <div className="hidden items-center gap-1 lg:flex">
              {isAuthenticated ? (
                <>
                  <Link to="/admin" className="btn-ghost !min-h-9 !px-3 text-sm">
                    Panel
                  </Link>
                  <button type="button" onClick={handleLogout} className="btn-ghost !min-h-9 !px-3 text-sm">
                    Salir
                  </button>
                </>
              ) : (
                <Link to="/login" className="btn-primary !min-h-9 !px-4 !py-1.5 text-sm">
                  Ingresar
                </Link>
              )}
            </div>
          </div>
        </div>
      </header>

      <nav
        className="fixed inset-x-0 bottom-0 z-40 border-t border-white/10 bg-chrome pb-[env(safe-area-inset-bottom)] text-on-chrome lg:hidden"
        aria-label="Navegación móvil"
      >
        <div className="mx-auto flex max-w-content items-stretch">
          {bottomPrimary.map((link) => (
            <NavLink
              key={link.slug}
              to={link.path}
              end={link.path === '/'}
              className={bottomLinkClass}
            >
              <Icon name={link.icon} />
              <span>{link.label}</span>
            </NavLink>
          ))}
          <button
            type="button"
            className={`flex min-h-[3.25rem] flex-1 flex-col items-center justify-center gap-0.5 text-[10px] font-semibold uppercase tracking-wide transition-colors ${
              moreOpen || moreLinks.some((l) => l.path === location.pathname) || location.pathname === '/login'
                ? 'text-primary'
                : 'text-on-chrome/70'
            }`}
            aria-expanded={moreOpen}
            aria-controls="mobile-more"
            onClick={() => setMoreOpen((o) => !o)}
          >
            <Icon name="more" />
            <span>Más</span>
          </button>
        </div>
      </nav>

      {moreOpen && (
        <div
          id="mobile-more"
          className="fixed inset-0 z-50 lg:hidden"
          role="dialog"
          aria-modal="true"
          aria-label="Más opciones"
        >
          <button
            type="button"
            className="absolute inset-0 bg-black/50"
            aria-label="Cerrar"
            onClick={() => setMoreOpen(false)}
          />
          <div className="absolute inset-x-0 bottom-0 rounded-t-2xl bg-chrome px-4 pb-[calc(4.5rem+env(safe-area-inset-bottom))] pt-4 text-on-chrome shadow-xl">
            <div className="mx-auto mb-4 h-1 w-10 rounded-full bg-white/30" />
            <nav className="flex flex-col gap-1" aria-label="Más enlaces">
              {moreLinks.map((link) => (
                <NavLink
                  key={link.slug}
                  to={link.path}
                  className={({ isActive }) =>
                    `flex min-h-12 items-center border-b border-white/10 font-display text-xl font-semibold uppercase tracking-wide ${
                      isActive ? 'text-primary' : 'text-on-chrome'
                    }`
                  }
                >
                  {link.label}
                </NavLink>
              ))}
            </nav>
            <div className="mt-5 flex flex-col gap-2">
              <button
                type="button"
                onClick={toggleTheme}
                className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-md border border-white/30 font-semibold text-on-chrome"
              >
                {mode === 'dark' ? '☀ Modo claro' : '☾ Modo oscuro'}
              </button>
              {isAuthenticated ? (
                <>
                  <Link to="/admin" className="btn-primary w-full">
                    Panel Admin
                  </Link>
                  <button
                    type="button"
                    onClick={handleLogout}
                    className="inline-flex min-h-11 w-full items-center justify-center rounded-md border border-white/30 font-semibold text-on-chrome"
                  >
                    Cerrar sesión
                  </button>
                </>
              ) : (
                <Link to="/login" className="btn-primary w-full">
                  Ingresar
                </Link>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PublicHeader;
